import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEtiquetas } from '../../hooks/useEtiquetas';
import { useUsuarios } from '../../hooks/useUsuarios';
import { HeaderList } from '../../components/HeaderList';
import { TableList } from '../../components/TableList';
import { Pagination } from '../../components/Pagination';
import { Badge } from '../../components/Badge';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const UsuarioHistorico: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [busca, setBusca] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const navigate = useNavigate();
  
  // Obter dados do usuário
  const { useUsuario } = useUsuarios();
  const { data: usuario, isLoading: usuarioLoading } = useUsuario(id || '');
  
  // Obter etiquetas relacionadas ao usuário (tanto entrada quanto saída)
  const { useEtiquetasPorUsuarioEntrada, useEtiquetasPorUsuarioSaida } = useEtiquetas();
  const { data: etiquetasEntrada = [], isLoading: loadingEntrada } = useEtiquetasPorUsuarioEntrada(id || '');
  const { data: etiquetasSaida = [], isLoading: loadingSaida } = useEtiquetasPorUsuarioSaida(id || '');
  
  // Combinar as etiquetas de entrada e saída
  const todasEtiquetas = [...etiquetasEntrada, ...etiquetasSaida];
  
  // Ordenar por data (mais recente primeiro)
  const etiquetasOrdenadas = [...todasEtiquetas].sort((a, b) => {
    return new Date(b.data).getTime() - new Date(a.data).getTime();
  });
  
  // Filtrar etiquetas com base na busca
  const etiquetasFiltradas = etiquetasOrdenadas.filter(etiqueta => 
    busca === '' || 
    etiqueta.observacao?.toLowerCase().includes(busca.toLowerCase()) ||
    etiqueta.nome_usuario_entrada.toLowerCase().includes(busca.toLowerCase()) ||
    etiqueta.nome_usuario_saida?.toLowerCase().includes(busca.toLowerCase()) ||
    format(new Date(etiqueta.data), 'dd/MM/yyyy').includes(busca)
  );
  
  // Cálculo da paginação
  const totalPages = Math.ceil(etiquetasFiltradas.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = etiquetasFiltradas.slice(indexOfFirstItem, indexOfLastItem);
  
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };
  
  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };
  
  // Resetar para primeira página quando houver uma nova busca
  useEffect(() => {
    setCurrentPage(1);
  }, [busca]);
  
  // Definição das colunas da tabela
  const columns = [
    { 
      header: 'Data', 
      accessor: 'data' as const,
      render: (etiqueta: any) => format(new Date(etiqueta.data), 'dd/MM/yyyy', { locale: ptBR })
    },
    { 
      header: 'Tipo', 
      accessor: 'id_usuario_entrada' as const,
      render: (etiqueta: any) => {
        const isEntrada = etiqueta.id_usuario_entrada === id;
        const variant = isEntrada ? 'green' : 'red';
        const tipo = isEntrada ? 'Entrada' : 'Saída';
        
        return (
          <Badge variant={variant} size="sm">
            {tipo}
          </Badge>
        );
      }
    },
    { 
      header: 'Quantidade', 
      accessor: 'quantidade' as const,
      render: (etiqueta: any) => {
        const isEntrada = etiqueta.id_usuario_entrada === id;
        const textColorClass = isEntrada ? 'text-green-600' : 'text-red-600';
        const prefix = isEntrada ? '+' : '-';
        
        return (
          <span className={textColorClass}>
            {prefix}{etiqueta.quantidade}
          </span>
        );
      }
    },
    { 
      header: 'Usuário', 
      accessor: 'nome_usuario_entrada' as const,
      render: (etiqueta: any) => {
        const isEntrada = etiqueta.id_usuario_entrada === id;
        return isEntrada ? etiqueta.nome_usuario_saida : etiqueta.nome_usuario_entrada;
      }
    },
    { 
      header: 'Observação', 
      accessor: 'observacao' as const,
      render: (etiqueta: any) => etiqueta.observacao || '-'
    },
  ];
  
  if (usuarioLoading || loadingEntrada || loadingSaida) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <HeaderList
        title={`Histórico de ${usuario?.nome || 'Usuário'}`}
        searchPlaceholder="Buscar no histórico..."
        searchValue={busca}
        onSearchChange={setBusca}
        showBackButton={true}
        onBackClick={() => navigate('/usuarios')}
      />
      
      <TableList
        data={currentItems}
        columns={columns}
      />
      
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={etiquetasFiltradas.length}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        indexOfFirstItem={indexOfFirstItem}
        indexOfLastItem={indexOfLastItem}
      />
    </div>
  );
};

export default UsuarioHistorico; 